import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { trackConversion } from '@/lib/tracking';

interface EngagementData {
  scrollDepth: number;
  timeOnPage: number;
  sessionId: string;
}

const generateSessionId = (): string => {
  const stored = sessionStorage.getItem('engagement_session_id');
  if (stored) return stored;
  const newId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  sessionStorage.setItem('engagement_session_id', newId);
  return newId;
};

// Routes where engagement tracking should NOT run
const EXCLUDED_ROUTES = ['/app', '/admin', '/login', '/signup'];

const isExcludedRoute = (pathname: string): boolean => {
  return EXCLUDED_ROUTES.some(route => pathname.startsWith(route));
};

export const useEngagementTracking = () => {
  const location = useLocation();
  const startTime = useRef<number>(Date.now());
  const maxScrollDepth = useRef<number>(0);
  const scrollMilestones = useRef<Set<number>>(new Set());
  const sessionId = useRef<string>(generateSessionId());
  const heartbeatInterval = useRef<NodeJS.Timeout | null>(null);
  const isExcluded = isExcludedRoute(location.pathname);

  const saveEvent = useCallback(async (_eventType: string, _eventData: Record<string, any>) => {
    // analytics_events INSERT desativado — manter tabela e leituras em /admin intactas.
    // GA4 (trackConversion) segue ativo nos callers. Para reativar: restaurar o insert abaixo.
    return;
  }, []);

  const trackScrollDepth = useCallback(() => {
    const scrollTop = window.scrollY;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    const scrollPercent = docHeight > 0 ? Math.round((scrollTop / docHeight) * 100) : 0;

    if (scrollPercent > maxScrollDepth.current) {
      maxScrollDepth.current = scrollPercent;
    }

    const milestones = [25, 50, 75, 100];
    milestones.forEach((milestone) => {
      if (scrollPercent >= milestone && !scrollMilestones.current.has(milestone)) {
        scrollMilestones.current.add(milestone);
        
        trackConversion('scroll_depth', {
          depth: milestone,
          page: location.pathname,
        });

        saveEvent('scroll_depth', {
          depth: milestone,
          page: location.pathname,
          maxDepth: maxScrollDepth.current,
        });
      }
    });
  }, [location.pathname, saveEvent]);

  const trackTimeOnPage = useCallback(() => {
    const timeSpent = Math.round((Date.now() - startTime.current) / 1000);
    
    trackConversion('time_on_page', {
      seconds: timeSpent,
      page: location.pathname,
    });

    saveEvent('time_on_page', {
      seconds: timeSpent,
      page: location.pathname,
      maxScrollDepth: maxScrollDepth.current,
    });
  }, [location.pathname, saveEvent]);

  const handleVisibilityChange = useCallback(() => {
    if (document.visibilityState === 'hidden') {
      trackTimeOnPage();
    }
  }, [trackTimeOnPage]);

  const handleBeforeUnload = useCallback(() => {
    trackTimeOnPage();
  }, [trackTimeOnPage]);

  useEffect(() => {
    // Skip tracking on system pages (app, admin, login, signup)
    if (isExcluded) {
      return;
    }

    // Reset on route change
    startTime.current = Date.now();
    maxScrollDepth.current = 0;
    scrollMilestones.current.clear();

    // Track page view
    saveEvent('page_view', {
      page: location.pathname,
      referrer: document.referrer,
    });

    // Scroll tracking
    window.addEventListener('scroll', trackScrollDepth, { passive: true });

    // Time tracking
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Heartbeat every 30 seconds
    heartbeatInterval.current = setInterval(() => {
      const timeSpent = Math.round((Date.now() - startTime.current) / 1000);
      saveEvent('heartbeat', {
        seconds: timeSpent,
        page: location.pathname,
        scrollDepth: maxScrollDepth.current,
      });
    }, 30000);

    return () => {
      window.removeEventListener('scroll', trackScrollDepth);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (heartbeatInterval.current) {
        clearInterval(heartbeatInterval.current);
      }
    };
  }, [location.pathname, trackScrollDepth, handleVisibilityChange, handleBeforeUnload, saveEvent, isExcluded]);

  return {
    sessionId: sessionId.current,
    getEngagementData: (): EngagementData => ({
      scrollDepth: maxScrollDepth.current,
      timeOnPage: Math.round((Date.now() - startTime.current) / 1000),
      sessionId: sessionId.current,
    }),
  };
};
